"use client";

import {
  LOCALE_COOKIE,
  SUPPORTED_LOCALES,
  coerceLocale,
  type Locale,
} from "@/i18n/locales";
import { dropConnectorViewParams } from "@/lib/connector-view-params";
import { useAppBridge, type WSContext } from "@/lib/embed-brige/embed-bridge";
import { useGlobalStore } from "@/store/global";
import { authSelectors } from "@/store/user/slices/auth/selectors";
import { useUserStore } from "@/store/user/user-store";
import { useWorkspaceStore } from "@/store/workspace/workspace-store";
import { handleWorkspaceNotFound } from "@/utils/workspace";
import { useRouter, useSearchParams } from "next/navigation";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type PropsWithChildren,
} from "react";
import { useShallow } from "zustand/react/shallow";

const EMBED_SESSION_STORAGE_KEY = "connector-embed-data";

interface EmbedData {
  debug: boolean;
  source: string | null;
  workspace_id: string | null;
  organization_id: string | null;
  inplace_copilot?: boolean;
  mode: string | null;
  app_id: number;
  is_iframe_mode: boolean;
}

const parseAppId = (value: string | null) => {
  if (!value) {
    return 0;
  }

  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : 0;
};

const readEmbedStateFromSessionStorage = (): EmbedData | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const storedValue = window.sessionStorage.getItem(
      EMBED_SESSION_STORAGE_KEY,
    );
    if (!storedValue) {
      return null;
    }
    const embedData: EmbedData = JSON.parse(storedValue);
    if (embedData.debug) {
      console.log(
        "[AppEmbedBridge] Read embed state from sessionStorage:",
        embedData,
      );
    }
    return embedData;
  } catch {
    return null;
  }
};

const saveEmbedStateToSessionStorage = (value: EmbedData): void => {
  if (typeof window === "undefined") {
    return;
  }
  if (value.debug) {
    console.log(
      "[AppEmbedBridge] Saving embed state to sessionStorage:",
      value,
    );
  }
  try {
    window.sessionStorage.setItem(
      EMBED_SESSION_STORAGE_KEY,
      JSON.stringify(value),
    );
  } catch {
    // Ignore storage failures so bridge setup can continue.
  }
};

const buildLocalizedHref = (
  locale: Locale,
  pathname: string,
  search: string,
) => {
  const segments = pathname.split("/").filter(Boolean);

  if (segments.length > 0) {
    const hasLocalePrefix = SUPPORTED_LOCALES.includes(segments[0] as Locale);
    if (hasLocalePrefix) {
      segments[0] = locale;
    } else {
      segments.unshift(locale);
    }
  } else {
    segments.push(locale);
  }

  const nextSearchParams = new URLSearchParams(search);
  nextSearchParams.set("lang", locale);

  const nextPath = `/${segments.join("/")}`;
  const nextSearch = nextSearchParams.toString();

  return nextSearch ? `${nextPath}?${nextSearch}` : nextPath;
};

const AppEmbedBridge: FC<PropsWithChildren> = ({ children }) => {
  const searchParams = useSearchParams();
  const router = useRouter();

  const [updateBridgeState, updateSystemStatus, bridgeState] = [
    useGlobalStore((state) => state.updateBridgeState),
    useGlobalStore((state) => state.updateSystemStatus),
    useGlobalStore((state) => state.bridge),
  ];

  const embedData: EmbedData = useMemo(() => {
    const embedDataFromSessionStorage = readEmbedStateFromSessionStorage();
    if (
      embedDataFromSessionStorage &&
      embedDataFromSessionStorage.mode === "iframe"
    ) {
      return embedDataFromSessionStorage;
    }

    const mode = searchParams.get("mode") ?? bridgeState.mode;
    const embedDataFromParams: EmbedData = {
      debug: searchParams.get("debug") === "true" || !!bridgeState.debug,
      source: searchParams.get("source") ?? bridgeState.source,
      workspace_id:
        searchParams.get("workspace_id") ?? bridgeState.workspaceId,
      organization_id:
        searchParams.get("organization_id") ?? bridgeState.organizationId,
      inplace_copilot:
        searchParams.get("inplace_copilot") === "true" ||
        bridgeState.isInplaceCopilot,
      mode,
      app_id: parseAppId(searchParams.get("app_id")) || bridgeState.appId,
      is_iframe_mode: mode === "iframe",
    };

    saveEmbedStateToSessionStorage(embedDataFromParams);

    return embedDataFromParams;
  }, [bridgeState, searchParams]);

  const [workspaces, setSelectedWorkspace] = useWorkspaceStore(
    useShallow((s) => [s.workspaces, s.setSelectedWorkspace]),
  );
  const isLoaded = useUserStore(authSelectors.isLoaded);
  const hasHandledInitialWorkspaceChange = useRef(false);
  const hasSentBridgeReady = useRef(false);
  const [isBridgeReady, setIsBridgeReady] = useState(false);
  if (embedData.debug && embedData.source) {
    console.log("[AppEmbedBridge] SEARCH_PARAMS app_id:", embedData.app_id);
  }

  useEffect(() => {
    if (!embedData.is_iframe_mode) {
      return;
    }
    updateBridgeState({
      appId: embedData.app_id,
      debug: embedData.debug,
      isInplaceCopilot: embedData.inplace_copilot,
      mode: embedData.mode ?? null,
      organizationId: embedData.organization_id ?? null,
      source: embedData.source ?? null,
      workspaceId: embedData.workspace_id ?? null,
    });
  }, [
    embedData.app_id,
    embedData.debug,
    embedData.mode,
    embedData.organization_id,
    embedData.source,
    embedData.workspace_id,
    updateBridgeState,
    embedData.is_iframe_mode,
  ]);

  useEffect(() => {
    if (!embedData.is_iframe_mode) return;
    updateSystemStatus({ showHeader: false });
    document.documentElement.style.setProperty("--app-header-offset", "0px");
  }, [embedData.is_iframe_mode, updateSystemStatus]);

  // Apply lang URL param on initial load so connector starts with the correct locale.
  const initialLang = useRef(searchParams.get("lang"));
  useEffect(() => {
    const lang = initialLang.current;
    if (!lang) return;
    const locale = coerceLocale(lang);
    document.cookie = `${LOCALE_COOKIE}=${locale};path=/;max-age=31536000;samesite=lax`;
    const nextHref = buildLocalizedHref(
      locale,
      window.location.pathname,
      window.location.search,
    );
    const currentHref = `${window.location.pathname}${window.location.search}`;

    if (nextHref !== currentHref) {
      router.replace(nextHref, { scroll: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (embedData.debug && embedData.source) {
    console.log("[AppEmbedBridge] SEARCH_PARAMS appId:", embedData.app_id);
  }

  // ============================== Locale switching ===============================
  // Follows the same pattern as ConditionalHeader.handleSelectLocale
  const switchLocale = (rawLocale: string) => {
    const locale = coerceLocale(rawLocale);
    document.cookie = `${LOCALE_COOKIE}=${locale};path=/;max-age=31536000;samesite=lax`;
    const nextHref = buildLocalizedHref(
      locale,
      window.location.pathname,
      window.location.search,
    );
    const currentHref = `${window.location.pathname}${window.location.search}`;

    if (nextHref !== currentHref) {
      router.replace(nextHref, { scroll: false });
    }
  };

  // ============================== Event handlers ===============================
  const handleLanguageChanged = (locale: string) => {
    if (embedData.debug) {
      console.log("[AppEmbedBridge] LANGUAGE_CHANGED:", locale);
    }
    switchLocale(locale);
  };

  const handleContextUpdate = (ctx: WSContext) => {
    if (embedData.debug) {
      console.log("[AppEmbedBridge] CONTEXT_UPDATED:", ctx);
    }
    if (!hasHandledInitialWorkspaceChange.current && ctx.workspace_id) {
      hasHandledInitialWorkspaceChange.current = true;
      handleWorkspaceChanged(ctx.workspace_id, ctx.workspace_name);
      if (embedData.debug) {
        console.log("[AppEmbedBridge] CONTEXT_UPDATED_INITIAL:", ctx);
      }
    }
  };

  const handleSessionExpired = () => {
    if (embedData.debug) {
      console.log("[AppEmbedBridge] SESSION_EXPIRED");
    }
    router.replace("/login");
  };

  const handleWorkspaceChanged = (
    workspaceId: string,
    workspaceName?: string,
  ) => {
    console.log("handleWorkspaceChanged", {
      workspaceId,
      organization_id: embedData.organization_id,
    });
    if (embedData.debug) {
      console.log(
        "[AppEmbedBridge] WORKSPACE_CHANGED:",
        workspaceId,
        workspaceName,
      );
    }
    const pathname = window.location.pathname;
    if (!workspaceId) return;

    updateBridgeState({ workspaceId });
    console.log("handleWorkspaceChanged", {
      workspaceId,
      organization_id: embedData.organization_id,
    });
    const ok = setSelectedWorkspace({
      workspace_id: workspaceId,
      organization_id: embedData.organization_id || "",
    });
    if (!ok) {
      if (embedData.debug) {
        console.log(
          "[AppEmbedBridge] WORKSPACE_CHANGED: failed to select workspace",
          workspaceId,
        );
      }
      handleReady();
      handleWorkspaceNotFound();
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParams.toString());
    const urlWorkspaceId =
      searchParams.get("workspace_id") ?? searchParams.get("workspace");
    if (urlWorkspaceId && urlWorkspaceId !== workspaceId) {
      // The new workspace's dashboard starts from its default view.
      dropConnectorViewParams(nextSearchParams);
    }
    nextSearchParams.set("workspace", workspaceId);

    const nextUrl =
      nextSearchParams.size > 0
        ? `${pathname}?${nextSearchParams.toString()}`
        : pathname;
    const currentSearch = searchParams.toString();
    const currentUrl = currentSearch
      ? `${pathname}?${currentSearch}`
      : pathname;

    if (nextUrl !== currentUrl) {
      router.replace(nextUrl, { scroll: false });
    }
  };

  // ============================== Bridge ===============================

  const { sendReady, sendHello } = useAppBridge({
    appId: embedData.app_id,
    debug: embedData.debug,
    onContextUpdate: handleContextUpdate,
    onLanguageChanged: handleLanguageChanged,
    onSessionExpired: handleSessionExpired,
    onWorkspaceChanged: handleWorkspaceChanged,
  });

  function handleReady() {
    sendReady("/", { debug: embedData.debug });
    if (embedData.debug) {
      console.log("[AppEmbedBridge] SEND_READY");
    }
    sendHello();
    if (embedData.debug) {
      console.log("[AppEmbedBridge] SEND_HELLO");
    }
    setIsBridgeReady(true);
  }

  useEffect(() => {
    if (embedData.debug) {
      console.log("[AppEmbedBridge] IS_LOADED:", {
        isLoaded,
        hasSentBridgeReady: hasSentBridgeReady.current,
      });
    }
    if (hasSentBridgeReady.current) return;
    hasSentBridgeReady.current = true;
    setTimeout(() => {
      handleReady();
    }, 1000);
  }, [embedData.debug, isLoaded, sendHello, sendReady]);

  if (embedData.is_iframe_mode && !isBridgeReady) {
    return null;
  }

  return <>{children}</>;
};

export default AppEmbedBridge;
